// from packages
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Container, Grid, Search, Icon, Button } from "semantic-ui-react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Truncate from "react-truncate";
import { toast } from "react-toastify";

// app level components
import SaveChanges from "components/ui/Buttons/SaveButton";

//actions
import {
  fetchCustomerDetails,
  updatingCustomerDetails,
} from "actions/downloadInvoice";

import cx from "classnames";
import _ from "lodash";
import styles from "./downloadInvoice.module.scss";

export default function DownloadInvoice() {
  const dispatch = useDispatch();

  //mapping state
  const accountId = useSelector((state) => _.get(state, "accounts.account.id"));
  const customerId = useSelector((state) =>
    _.get(state, "accounts.account.customerId")
  );
  const externalInvoiceData = useSelector((state) =>
    _.get(state, "downloadInvoice.externalInvoiceData")
  );
  const externalDataList = _.get(externalInvoiceData, "items");
  const loading = _.get(externalInvoiceData, "fetching");

  //state
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(customerId);
  const [initialCustomerId, setInitialCustomerId] = useState(customerId);
  const [disableSave, setDisableSave] = useState(true);
  const [initialRender, setInitialRender] = useState(true);

  useEffect(() => {
    dispatch(fetchCustomerDetails({ limit: 20, query: searchQuery }));
  }, [searchQuery]);

  useEffect(() => {
    setInitialRender(false);
  }, []);

  useEffect(() => {
    if (customerId) {
      setSelectedCustomerId(customerId);
      setInitialCustomerId(customerId);
    }
  }, [customerId]);

  useEffect(() => {
    if (_.isEqual(initialCustomerId, selectedCustomerId)) {
      setDisableSave(true);
    } else {
      setDisableSave(false);
    }
  }, [selectedCustomerId]);

  useEffect(() => {
    if (!initialRender) {
      if (
        !_.get(externalInvoiceData, "updating") &&
        _.get(externalInvoiceData, "updated")
      ) {
        toast.success(`Successfully updated ${selectedCustomerId}`);
      } else if (
        !_.get(externalInvoiceData, "updating") &&
        !_.get(externalInvoiceData, "updated") &&
        _.get(externalInvoiceData, "error")
      ) {
        toast.error(`Failed to update ${selectedCustomerId}`);
      }
    }
  }, [_.get(externalInvoiceData, "updating")]);

  const handleSearchChange = (e, { value }) => {
    setSearchQuery(value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    handleSearchClick();
  };

  const handleSearchClick = () => {
    dispatch(fetchCustomerDetails({ limit: 20, query: searchQuery }));
  };

  const handleSaveChanges = () => {
    dispatch(
      updatingCustomerDetails({
        accountId: accountId,
        customerId: selectedCustomerId,
      })
    );

    setInitialCustomerId(selectedCustomerId);
    setDisableSave(true);
  };

  const handleReset = () => {
    setSearchQuery("");
    setSelectedCustomerId(initialCustomerId);
    setDisableSave(true);
  };

  const handleItemClick = (customerId) => {
    setSelectedCustomerId(customerId);
  };

  return (
    <Container className={styles.container}>
      <div className={cx(styles.title)}>Chargebee Customer ID</div>
      <Grid>
        <Grid.Column width={6} textAlign="right">
          <form onSubmit={handleSearchSubmit} className={cx(styles.search)}>
            <Search
              id="search"
              input={{
                icon: (
                  <Icon circular name="search" onClick={handleSearchClick} />
                ),
              }}
              showNoResults={false}
              value={searchQuery}
              onSearchChange={handleSearchChange}
              placeholder="Search by company name"
            />
          </form>
        </Grid.Column>
      </Grid>
      <Container className={styles.dataContainer}>
        {loading ? (
          <div className={styles.loadingMessage}>Loading...</div>
        ) : _.size(externalDataList) === 0 ? (
          <div className={styles.noResults}>No results found</div>
        ) : (
          externalDataList.map((item) => (
            <div
              key={item.id}
              className={cx(styles.listItem)}
              onClick={() => handleItemClick(item.id)}
            >
              <i
                className={cx(
                  "fa",
                  selectedCustomerId === item.id
                    ? "fa-dot-circle-o"
                    : "fa-circle-o",
                  styles.customIcon
                )}
                aria-hidden="true"
              ></i>
              <span className={styles.customerId}>{item.id}</span>
              <span className={styles.companyName}>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip
                      id={`tooltip-${item.id}`}
                      className={styles.accountNameTooltip}
                    >
                      {item.company}
                    </Tooltip>
                  }
                >
                  <Truncate lines={1} width={200} ellipsis={<span>...</span>}>
                    {item.company}
                  </Truncate>
                </OverlayTrigger>
              </span>
            </div>
          ))
        )}
      </Container>
      <Container className={styles.saveChange}>
        <span className={styles.saveChanges}>
          <SaveChanges onClick={handleSaveChanges} disabled={disableSave} />
          <Button onClick={handleReset} className={cx(styles.resetButton)}>
            Reset
          </Button>
        </span>
      </Container>
    </Container>
  );
}
